import { useCallback } from 'react'
import { useStore } from 'react-redux'
import { useNavigate, type NavigateOptions } from 'react-router-dom'
import { postCreateAgentV2Conversation } from '@/agent-v2/conversations/fetch/post-create-agent-v2-conversation'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { AgentV2ConversationSurface, AgentV2CreateConversationResponse } from '@/agent-v2/store/schemas'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { useAppDispatch } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { kRouteSegments } from '@/routing/paths'

export function useCreateNewAgentV2Chat() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const store = useStore<RootState>()

  const createNewAgentV2Chat = useCallback(
    async (navigateOnSuccess: boolean, matterId: string, onSuccess?: () => void, onError?: (e: unknown) => void, navigationOptions?: NavigateOptions) => {
      const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration

      try {
        const response: AgentV2CreateConversationResponse = await postCreateAgentV2Conversation(matterId, AgentV2ConversationSurface.WEB)
        if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return undefined

        dispatch(AgentV2ConversationsActions.upsertConversationFromSummary(response))
        dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true }))
        onSuccess?.()

        if (navigateOnSuccess) {
          const conversationPath = buildConversationPath(matterId, response.id, kRouteSegments.agentV2)
          if (navigationOptions) navigate(conversationPath, navigationOptions)
          else navigate(conversationPath)
        }

        return response
      } catch (e) {
        if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return undefined
        onError?.(e)
        return undefined
      }
    },
    [dispatch, navigate, store]
  )

  return { createNewAgentV2Chat }
}
